<template>
  <Modal
    v-model="open"
    :max-width="600"
    :fullscreen="$vuetify.display.xs"
    :loading="loading"
  >
    <template v-slot:activator="{ props }">
      <v-list-item
        v-if="!isCrewMember"
        :title="t('whats-new')"
        class="ma-0"
        v-bind="props"
      >
        <template v-slot:prepend>
          <v-icon>mdi-new-box</v-icon>
        </template>
        <template v-slot:append>
          <v-badge v-if="isSomethingNew" inline dot color="error" />
        </template>
      </v-list-item>
    </template>
    <template v-slot:title>
      {{ t('whats-new') }}
    </template>
    <template v-slot:content>
      <v-select
        v-if="releases.length"
        v-model="selectedRelease"
        :items="releases"
        return-object
        variant="outlined"
        density="compact"
      />
      <div v-if="!loading && !selectedRelease" class="loading-container">
        {{ t('nothing-here') }}
        <v-icon>mdi-robot-happy-outline</v-icon>
      </div>
      <div v-html="renderedMarkdown"></div>
      <div
        v-if="formattedPublishDate"
        class="published-date"
        :class="{ 'bg-warning': !selectedRelease.isReleased }"
      >
        {{ t('published') }}: {{ formattedPublishDate }}
      </div>
    </template>
    <template v-slot:actions v-if="isAdmin">
      <v-btn color="primary" @click="editPost" v-if="selectedRelease">
        Edit
      </v-btn>
      <v-spacer />
      <CreatePost
        v-if="isAdmin"
        @refresh="refresh"
        :selectedPost="selectedPost"
      />
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRootStore } from '../../../stores/root';
import Modal from '../../Modal.vue';
import { storeToRefs } from 'pinia';
import { getAPI } from './whatsnewapi';
import CreatePost from './CreatePost.vue';
import { md } from './markdown';
import { useI18n } from 'vue-i18n';
const { t } = useI18n();

const store = useRootStore();
const { isAdmin, enterpriseClient, isCrewMember } = storeToRefs(store);

const open = ref(false);
const loading = ref(true);
const releases = ref([]);
const selectedRelease = ref();

const renderedMarkdown = computed(() => {
  return md.render(selectedRelease.value?.content || '');
});

const formattedPublishDate = computed(() => {
  if (!selectedRelease.value) return '';
  if (selectedRelease.value.isReleased) {
    return new Date(
      selectedRelease.value.releaseDate || selectedRelease.value.SK,
    ).toLocaleDateString();
  }
  return formatDate(
    selectedRelease.value.releaseDate || selectedRelease.value.SK,
  );
});

const selectedPost = ref();
function editPost() {
  selectedPost.value = selectedRelease.value;
}

function refresh() {
  selectedPost.value = null;
  getReleases();
}

function formatDate(date) {
  return new Date(date).toLocaleString({
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

async function getReleases() {
  loading.value = true;
  try {
    const data = await getAPI({
      url: '',
      params: {
        enterprise: enterpriseClient.value,
      }
    });
    releases.value = data.map((release) => ({
      ...release,
      isReleased: release.releaseDate
        ? new Date(release.releaseDate) < new Date()
        : true,
    }));
    selectedRelease.value = releases.value[0];
    isSomethingNew.value = false;
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
}
watch(open, () => {
  if (open.value) {
    if (selectedRelease.value) return;
    getReleases();
  }
});

const isSomethingNew = ref(false);
async function getIsMostRecentRead() {
  if (!enterpriseClient.value) return;
  const data = await getAPI({
    url: 'isLatestRead',
    params: { enterprise: enterpriseClient.value },
  });
  isSomethingNew.value = !data.isLatestRead;
}
watch(enterpriseClient, getIsMostRecentRead, { immediate: true });
</script>
<i18n>
{
  "en": {
    "whats-new": "What's New",
    "nothing-here": "Nothing Here",
    "published": "Publish Date",
    "ok-don-t-show-again": "Don't Show Again"
  },
  "es": {
    "whats-new": "Qué Hay de Nuevo",
    "nothing-here": "No Hay Nada Aquí",
    "published": "Fecha de Publicación",
    "ok-don-t-show-again": "No Mostrar de Nuevo"
  }
}
</i18n>
<style scoped>
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}
.published-date {
  text-align: right;
  font-size: 0.8rem;
  color: rgba(var(--v-theme-on-surface), 0.5);
}
:deep(ul),
:deep(ol) {
  padding-inline-start: 40px;
}
:deep(.v-toolbar-title__placeholder) {
  width: fit-content;
}
:deep(img) {
  width: 100%;
}
</style>
